/**
 * just to get better types from mathjs
 */

import { AliasUnit } from '@shared/models/units/alias-units.model';
import { CO2eBaseUnit } from '@shared/models/units/co2e-units.model';
import { CurrencyBaseUnit } from '@shared/models/units/currency-units.model';
import { SupportedMetricUnit } from '@shared/models/units/metric-units.model';
import { UnitCategory } from '@shared/models/units/unit-base.model';

import type { MathJsInstance } from 'mathjs';

type SupportedUnit =
  SupportedMetricUnit | CurrencyBaseUnit | CO2eBaseUnit | AliasUnit;
export type MathJSUnitInstance = ReturnType<MathJsInstance['unit']>[0];

/** these are validated via unitToMathJsBaseTypeMap.test.ts */
/**
 * They are needed to transform aliased units to their canonical unit
 */
export const unitToMathJsBaseTypeTranslationMap = {
  m: {
    mathJsBaseType: 'LENGTH',
    power: 1,
    nodonCategory: UnitCategory.Length,
  },
  'm^2': {
    mathJsBaseType: 'LENGTH',
    power: 2,
    nodonCategory: UnitCategory.Area,
  },
  'm^3': {
    mathJsBaseType: 'LENGTH',
    power: 3,
    nodonCategory: UnitCategory.Volume,
  },
  g: {
    mathJsBaseType: 'MASS',
    power: 1,
    nodonCategory: UnitCategory.Mass,
  },
  s: {
    mathJsBaseType: 'TIME',
    power: 1,
    nodonCategory: UnitCategory.Time,
  },
  A: {
    mathJsBaseType: 'CURRENT',
    power: 1,
    nodonCategory: UnitCategory.ElectricCurrent,
  },
  K: {
    mathJsBaseType: 'TEMPERATURE',
    power: 1,
    nodonCategory: UnitCategory.Temperature,
  },
  cd: {
    mathJsBaseType: 'LUMINOUS_INTENSITY',
    power: 1,
    nodonCategory: UnitCategory.Light,
  },
  Hz: {
    mathJsBaseType: 'FREQUENCY',
    power: 1,
    nodonCategory: UnitCategory.Frequency,
  },
  N: {
    mathJsBaseType: 'MASS', // this is only because we've overridden N to be defined through kilograms
    power: 1,
    nodonCategory: UnitCategory.Mass,
  },
  Pa: {
    mathJsBaseType: 'PRESSURE',
    power: 1,
    nodonCategory: UnitCategory.Pressure,
  },
  J: {
    mathJsBaseType: 'ENERGY',
    power: 1,
    nodonCategory: UnitCategory.Energy,
  },
  W: {
    mathJsBaseType: 'POWER',
    power: 1,
    nodonCategory: UnitCategory.Power,
  },
  Wh: {
    mathJsBaseType: 'ENERGY',
    power: 1,
    nodonCategory: UnitCategory.Energy,
  },
  V: {
    mathJsBaseType: 'ELECTRIC_POTENTIAL',
    power: 1,
    nodonCategory: UnitCategory.ElectricPotential,
  },
  ohm: {
    mathJsBaseType: 'ELECTRIC_RESISTANCE',
    power: 1,
    nodonCategory: UnitCategory.ElectricResistance,
  },
  bar: {
    mathJsBaseType: 'PRESSURE',
    power: 1,
    nodonCategory: UnitCategory.Pressure,
  },
  t: {
    mathJsBaseType: 'MASS',
    power: 1,
    nodonCategory: UnitCategory.Mass,
  },
  C: {
    mathJsBaseType: 'ELECTRIC_CHARGE',
    power: 1,
    nodonCategory: UnitCategory.ElectricCharge,
  },
  H: {
    mathJsBaseType: 'ELECTRIC_INDUCTANCE',
    power: 1,
    nodonCategory: UnitCategory.ElectricInductance,
  },
  l: {
    mathJsBaseType: 'VOLUME',
    power: 1,
    nodonCategory: UnitCategory.Volume,
  },
  mol: {
    mathJsBaseType: 'AMOUNT_OF_SUBSTANCE',
    power: 1,
    nodonCategory: UnitCategory.Amount,
  },
  S: {
    mathJsBaseType: 'ELECTRIC_CONDUCTANCE',
    power: 1,
    nodonCategory: UnitCategory.ElectricConductance,
  },
  SEK: {
    mathJsBaseType: 'SEK_STUFF',
    power: 1,
    nodonCategory: UnitCategory.CurrencySEK,
  },
  USD: {
    mathJsBaseType: 'SEK_STUFF',
    power: 1,
    nodonCategory: UnitCategory.CurrencyUSD,
  },
  EUR: {
    mathJsBaseType: 'SEK_STUFF',
    power: 1,
    nodonCategory: UnitCategory.CurrencyEUR,
  },
  gCO2e: {
    mathJsBaseType: 'gCO2e_STUFF',
    power: 1,
    nodonCategory: UnitCategory.CO2e,
  },
  tCO2e: {
    mathJsBaseType: 'gCO2e_STUFF',
    power: 1,
    nodonCategory: UnitCategory.CO2e,
  },
  co2e: {
    mathJsBaseType: 'gCO2e_STUFF',
    power: 1,
    nodonCategory: UnitCategory.CO2e,
  },
  CO2e: {
    mathJsBaseType: 'gCO2e_STUFF',
    power: 1,
    nodonCategory: UnitCategory.CO2e,
  },
  '°C': {
    mathJsBaseType: 'TEMPERATURE',
    power: 1,
    nodonCategory: UnitCategory.Temperature,
  },
  h: {
    mathJsBaseType: 'TIME',
    power: 1,
    nodonCategory: UnitCategory.Time,
  },
  's^2': {
    mathJsBaseType: 'TIME',
    power: 2,
    nodonCategory: UnitCategory.Time,
  },
  rad: {
    mathJsBaseType: 'ANGLE',
    power: 1,
    nodonCategory: UnitCategory.Angle,
  },
  year: {
    mathJsBaseType: 'TIME',
    power: 1,
    nodonCategory: UnitCategory.Time,
  },
  yr: {
    mathJsBaseType: 'TIME',
    power: 1,
    nodonCategory: UnitCategory.Time,
  },
  kg: {
    mathJsBaseType: 'MASS',
    power: 1,
    nodonCategory: UnitCategory.Mass,
  },
  pcs: {
    mathJsBaseType: 'NONE',
    power: 1,
    nodonCategory: UnitCategory.Amount,
  },
  unit: {
    mathJsBaseType: 'NONE',
    power: 1,
    nodonCategory: UnitCategory.Amount,
  },
  people: {
    mathJsBaseType: 'NONE',
    power: 1,
    nodonCategory: UnitCategory.Amount,
  },
  activity: {
    mathJsBaseType: 'NONE',
    power: 1,
    nodonCategory: UnitCategory.Amount,
  },
  GFA: {
    mathJsBaseType: 'SURFACE',
    power: 1,
    nodonCategory: UnitCategory.Area,
  },
  '°F': {
    mathJsBaseType: 'TEMPERATURE',
    power: 1,
    nodonCategory: UnitCategory.Temperature,
  },
  days: {
    mathJsBaseType: 'TIME',
    power: 1,
    nodonCategory: UnitCategory.Time,
  },
  deg: {
    mathJsBaseType: 'ANGLE',
    power: 1,
    nodonCategory: UnitCategory.Angle,
  },
  min: {
    mathJsBaseType: 'TIME',
    power: 1,
    nodonCategory: UnitCategory.Time,
  },
} as const satisfies Record<SupportedUnit, MathJSBaseTypeToUnitCategoryData>;

export type MathJSBaseTypeToUnitCategoryData = {
  mathJsBaseType: string;
  power: number;
  nodonCategory: UnitCategory;
};
type UnitToMathJSBaseTypeMap = typeof unitToMathJsBaseTypeTranslationMap;
export type MathJSBaseType =
  UnitToMathJSBaseTypeMap[keyof UnitToMathJSBaseTypeMap];
